import { Link, useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import GoldThreadPanel from "@/components/GoldThread";
import GoldThreadWeave from "@/components/GoldThreadWeave";
import { SunArc } from "@/components/SparkSun";
import { PersonalSun } from "@/components/PersonalSun";
import { NextAction } from "@/components/NextAction";
import { computeJourney, STATE_LABEL } from "@/lib/journey";
import { activityPath } from "@/lib/activities";
import { Compass } from "lucide-react";
import { freshConversation } from "@/lib/api";

const JOURNEY = [
  { key: "Start Here", title: "Start here", subtitle: "Why are you here?" },
  { key: "Understand Me", title: "Understand me", subtitle: "Where am I now?" },
  { key: "What I Bring", title: "Discover what I bring", subtitle: "Strengths, achievements, values." },
  { key: "Unblock Thinking", title: "Unblock my thinking", subtitle: "Confidence and assumptions." },
  { key: "Work Out What I Want", title: "Work out what I want", subtitle: "Priorities and criteria." },
  { key: "Explore Options", title: "Explore my options", subtitle: "Stay · Reshape · Step Up · Move · Pivot · Reinvent." },
  { key: "Choose Direction", title: "Choose my direction", subtitle: "Bring the learning together." },
];

export default function Dashboard() {
  const { profile, isDev, userId, setProfile } = useSpark();
  const nav = useNavigate();
  if (!profile) return <div className="text-[#8A8A91]">Loading your journey…</div>;

  const first = profile.display_name || "you";
  const { items } = computeJourney(profile);
  const stageState = (stage) => {
    const acts = items.filter(a => a.stage === stage.key && a.state !== "anytime");
    if (acts.some(a => a.state === "here")) return "here";
    if (acts.every(a => a.state === "completed")) return "done";
    if (acts.some(a => a.state === "completed" || a.state === "in-progress")) return "in-progress";
    return "not-started";
  };

  return (
    <div className="space-y-16">
      {/* Hero — editorial, no card wall */}
      <section className="relative">
        <div className="absolute -right-8 -top-12 opacity-[0.18] pointer-events-none hidden md:block">
          <SunArc size={240} color="#C3A56B" strokeWidth={1} />
        </div>
        <p className="spark-eyebrow-copper spark-eyebrow-with-dot">
          {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
        </p>
        <h1 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium">
          {profile.onboarded ? <>Welcome back, <span className="spark-voice text-[#1F3D80]">{first}.</span></> : <>Welcome to <span className="spark-voice text-[#1F3D80]">Spark.</span></>}
        </h1>
        {profile.starting_point_summary && (
          <p className="mt-6 max-w-3xl spark-voice text-2xl leading-snug text-[#17171C]">
            {profile.starting_point_summary}
          </p>
        )}
      </section>

      <NextAction />

      <div className="flex flex-wrap gap-3 -mt-8">
        <Link data-testid="cta-open-compass" to="/compass" className="text-sm text-[#8A8A91] hover:text-[#17171C] inline-flex items-center gap-2">
          <Compass size={14} /> Open my Career Compass
        </Link>
        {isDev && (
          <button data-testid="dev-c1-fresh-btn" onClick={async () => { await freshConversation(userId); setProfile({ ...profile, c1_active: true }); nav("/conversation/why-am-i-here"); }} className="text-sm text-[#8A8A91] hover:text-[#1F3D80]">Start Conversation 1 fresh (dev)</button>
        )}
      </div>

      <div className="spark-thread" />

      <PersonalSun />

      <div className="spark-thread" />

      <GoldThreadPanel />

      <GoldThreadWeave compact />

      <section>
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="spark-eyebrow">Your journey</p>
            <h2 className="mt-2 text-3xl text-[#17171C] font-medium tracking-tight">Where you are, right now.</h2>
          </div>
          <button
            data-testid="jump-to-part-two-btn"
            onClick={() => nav("/part-two")}
            className="hidden sm:inline-flex text-sm text-[#8A8A91] hover:text-[#17171C]"
          >
            Ready to make your move? →
          </button>
        </div>

        {/* Journey list — editorial, connected by a fine gold thread rather than boxed cards */}
        <ol className="relative">
          <div className="absolute left-[9px] top-3 bottom-3 w-px"
               style={{ background: "linear-gradient(180deg, rgba(195,165,107,0.05) 0%, rgba(195,165,107,0.55) 15%, rgba(195,165,107,0.55) 85%, rgba(195,165,107,0.05) 100%)" }} />
          {JOURNEY.map((stage) => {
            const state = stageState(stage);
            const acts = items.filter(a => a.stage === stage.key);
            return (
              <li key={stage.key} className="relative pl-10 py-4">
                <span
                  className={`absolute left-0 top-6 h-[18px] w-[18px] rounded-full border ${
                    state === "done" ? "border-transparent" : state === "here" || state === "in-progress" ? "border-[#2C50A0]" : "border-[rgba(0,0,0,0.18)]"
                  }`}
                  style={{ background: state === "done" ? "#2C50A0" : state === "here" || state === "in-progress" ? "hsl(var(--spark-warmth))" : "white" }}
                />
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <h3 className="text-2xl text-[#17171C] font-medium tracking-tight">{stage.title}</h3>
                  <span className="spark-voice text-[#56565D] text-lg">{stage.subtitle}</span>
                  {state === "done" && <span className="text-xs text-[#1F3D80]">Completed ✓</span>}
                </div>
                <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {acts.map(a => (
                    <li key={a.key}>
                      <Link to={activityPath(a)} data-testid={`journey-activity-${a.key}`} data-state={a.state}
                            className={`inline-flex items-center gap-1.5 hover:text-[#1F3D80] ${a.state === "here" ? "text-[#17171C] font-medium" : a.state === "completed" ? "text-[#8A8A91] line-through decoration-[rgba(0,0,0,0.25)]" : "text-[#56565D]"}`}>
                        {a.title}
                        {STATE_LABEL[a.state] && <span className={`text-[10px] uppercase tracking-[0.15em] ${a.state === "here" ? "text-[#1F3D80]" : "text-[#8A8A91]"}`}>· {STATE_LABEL[a.state]}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-5">
          <div>
            <p className="spark-eyebrow-copper mb-2">When you're ready</p>
            <p className="spark-voice text-3xl text-[#17171C] leading-snug">Okay. What's next?</p>
            <p className="text-[#56565D] mt-2 max-w-md">Turn what you've discovered into your next move.</p>
          </div>
          <Link to="/part-two" data-testid="dashboard-part-two-btn" className="spark-btn-secondary">Make my move</Link>
        </div>
      </section>
    </div>
  );
}
